import React, { useEffect } from "react";
import { useStudio } from "../store/useStudio";
import { TYPOLOGIES, occupancyBaselines, cropMixYieldPerM2Yr } from "../lib/kbLogic";
import { OCCUPANT_DENSITY } from "../data/ashrae";
import { InfoTip } from "./InfoTip";
import { colors, fonts, shadows } from "../theme";

// Section 1: Building typology + occupancy configuration. Drives the Location ring's
// dynamic opportunity area and the Assistant's per-capita coverage / waste baselines.
export default function BuildingConfigPanel({ annualYieldKg = 0 }) {
  const buildingTypology = useStudio((s) => s.buildingTypology);
  const occupancy = useStudio((s) => s.occupancy);
  const opportunity = useStudio((s) => s.opportunity);
  const setTypology = useStudio((s) => s.setTypology);
  const setOccupancy = useStudio((s) => s.setOccupancy);
  const occupancyMode = useStudio((s) => s.occupancyMode);
  const gfa = useStudio((s) => s.gfa);
  const occDensity = useStudio((s) => s.occDensity);
  const setOccupancyMode = useStudio((s) => s.setOccupancyMode);
  const setGfa = useStudio((s) => s.setGfa);
  const setOccDensity = useStudio((s) => s.setOccDensity);
  const mepReserve = useStudio((s) => s.mepReserve);
  const shadowPct = useStudio((s) => s.shadowPct);
  const shadowAuto = useStudio((s) => s.shadowAuto);
  const setMepReserve = useStudio((s) => s.setMepReserve);
  const setShadowPct = useStudio((s) => s.setShadowPct);
  const setShadowAuto = useStudio((s) => s.setShadowAuto);
  const mepAuto = useStudio((s) => s.mepAuto);
  const mepRoofPct = useStudio((s) => s.mepRoofPct);
  const setMepAuto = useStudio((s) => s.setMepAuto);
  const setMepRoofPct = useStudio((s) => s.setMepRoofPct);
  const grossAreas = useStudio((s) => s.grossAreas);
  const scope = useStudio((s) => s.scope);
  const selectedLocs = useStudio((s) => s.selections.locations);
  const kb = useStudio((s) => s.kb);
  const cropSel = useStudio((s) => s.selections.crops);
  const yieldDensity = React.useMemo(() => cropMixYieldPerM2Yr(kb, { crops: cropSel }), [kb, cropSel]);

  // Auto-Calc: contextual overshadowing is computed by the 3D engine — the fraction of the building
  // envelope shaded by imported CONTEXT masses traced across the site's sun path (location + North
  // aware). No context geometry → honest 0% (zero fake math). null until the engine has computed it.
  const contextShade = useStudio((s) => s.contextShade);
  const autoShadow = contextShade == null ? 0 : contextShade;
  useEffect(() => {
    if (shadowAuto && contextShade != null && Math.abs((shadowPct || 0) - autoShadow) > 0.001) setShadowPct(autoShadow);
  }, [shadowAuto, autoShadow, contextShade, shadowPct, setShadowPct]);

  // Auto-Calc: reserve = roof area × mepRoofPct. Real geometry only — no fabricated value.
  const roofArea = grossAreas?.roof || 0;
  const autoMep = Math.round(roofArea * (mepRoofPct || 0));
  useEffect(() => {
    if (mepAuto && roofArea > 0 && Math.abs((mepReserve || 0) - autoMep) > 0.5) setMepReserve(autoMep);
  }, [mepAuto, autoMep, roofArea, mepReserve, setMepReserve]);

  // Density mode: people = GFA ÷ (m²/person). Kept in sync so all downstream calcs use one number.
  const derivedPeople = occupancyMode === "density" && gfa > 0 && occDensity > 0 ? Math.round(gfa / occDensity) : null;
  useEffect(() => {
    if (occupancyMode === "density" && derivedPeople != null && derivedPeople !== occupancy) setOccupancy(derivedPeople);
  }, [occupancyMode, derivedPeople, occupancy, setOccupancy]);

  const baselines = occupancyBaselines(buildingTypology, occupancy, annualYieldKg);

  return (
    <section style={styles.card} data-testid="building-config-panel">
      <div style={styles.head}>
        <span style={{ ...styles.title, display: "inline-flex", alignItems: "center", gap: 5 }}>
          BUILDING TYPOLOGY &amp; OCCUPANCY
          <InfoTip testId="typology-info" title="Building Typology & Occupancy" body="Calibrates yield coverage and waste baselines from the building's use type and how many people it serves." placement="bottom" />
        </span>
      </div>

      <label style={styles.fieldLabel}>Building Typology</label>
      <select
        style={styles.select}
        value={buildingTypology || ""}
        onChange={(e) => setTypology(e.target.value || null)}
        data-testid="typology-select"
      >
        <option value="">Select typology…</option>
        {TYPOLOGIES.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>

      <div style={styles.occHeadRow}>
        <label style={{ ...styles.fieldLabel, marginBottom: 0 }}>Estimated Occupancy</label>
        <div style={styles.occModeToggle} data-testid="occupancy-mode-toggle">
          <button type="button" data-testid="occ-mode-people"
            onClick={() => setOccupancyMode("people")}
            style={{ ...styles.occModeBtn, ...(occupancyMode !== "density" ? styles.occModeBtnOn : {}) }}>Total people</button>
          <button type="button" data-testid="occ-mode-density"
            onClick={() => setOccupancyMode("density")}
            style={{ ...styles.occModeBtn, ...(occupancyMode === "density" ? styles.occModeBtnOn : {}) }}>By density</button>
        </div>
      </div>
      {occupancyMode !== "density" ? (
        <div style={styles.occupancyRow}>
          <input
            type="number"
            min="0"
            value={occupancy ?? ""}
            placeholder="e.g. 120"
            onChange={(e) => setOccupancy(e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0))}
            style={styles.input}
            data-testid="occupancy-input"
          />
          <span style={styles.unit}>people</span>
        </div>
      ) : (
        <div style={styles.densityWrap} data-testid="occupancy-density-block">
          <select style={styles.select} data-testid="ashrae-density-select" defaultValue=""
            onChange={(e) => { const o = OCCUPANT_DENSITY.find((x) => x.id === e.target.value); if (o) setOccDensity(o.m2PerPerson); }}>
            <option value="">ASHRAE 62.1 default by space type…</option>
            {OCCUPANT_DENSITY.map((o) => (
              <option key={o.id} value={o.id}>{o.label} — {o.m2PerPerson} m²/person</option>
            ))}
          </select>
          <div style={styles.densityRow}>
            <div style={styles.densityCol}>
              <span style={styles.densityMini}>Gross floor area</span>
              <div style={styles.occupancyRow}>
                <input type="number" min="0" value={gfa ?? ""} placeholder="e.g. 4000"
                  onChange={(e) => setGfa(e.target.value)} style={styles.input} data-testid="gfa-input" />
                <span style={styles.unit}>m²</span>
              </div>
            </div>
            <div style={styles.densityCol}>
              <span style={styles.densityMini}>Density</span>
              <div style={styles.occupancyRow}>
                <input type="number" min="0" step="0.1" value={occDensity ?? ""} placeholder="e.g. 18.6"
                  onChange={(e) => setOccDensity(e.target.value)} style={styles.input} data-testid="occ-density-input" />
                <span style={styles.unit}>m²/pp</span>
              </div>
            </div>
          </div>
          <div style={styles.densityResult} data-testid="occupancy-density-result">
            {derivedPeople != null
              ? <>≈ <strong>{derivedPeople.toLocaleString()}</strong> people ({gfa.toLocaleString()} m² ÷ {occDensity} m²/person)</>
              : "Enter floor area and density (or pick a space type) to compute occupancy."}
          </div>
          <div style={styles.densitySrc}>Source: ASHRAE 62.1 Table 6.2.2.1 default occupant density (ventilation standard). ASHRAE 90.1 (energy) references the same space types.</div>
        </div>
      )}

      <div style={styles.mepRow}>
        <div style={{ flex: 1 }}>
          <div style={styles.shadowLabelRow}>
            <label style={{ ...styles.fieldLabel, marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 5 }}>
              Roof Reserve
              <InfoTip
                testId="mep-reserve-info"
                title="Roof Reserve"
                body="Roof area set aside for rooftop plant, PV and access — excluded from the growable area. With Auto-Calc on, it's a % of total roof area applied to your model geometry."
              />
            </label>
            <button type="button" data-testid="mep-auto-toggle"
              disabled={roofArea <= 0}
              onClick={() => setMepAuto(!mepAuto)}
              style={{ ...styles.autoBtn, ...(mepAuto ? styles.autoBtnOn : {}), ...(roofArea <= 0 ? styles.autoBtnDisabled : {}) }}
              title={roofArea > 0 ? "Auto-calc reserve as a share of roof area" : "Load a building model to auto-calc from roof area"}>
              {mepAuto ? "Auto ✓" : "Auto"}
            </button>
          </div>
          <div style={styles.occupancyRow}>
            <input type="number" min="0" disabled={mepAuto} value={mepReserve || ""} placeholder="0"
              onChange={(e) => setMepReserve(e.target.value)}
              style={{ ...styles.input, ...(mepAuto ? styles.inputLocked : {}) }} data-testid="mep-reserve-input" />
            <span style={styles.unit}>m²</span>
          </div>
          {mepAuto && (
            <div style={styles.mepAutoRow} data-testid="mep-auto-detail">
              <input type="number" min="0" max="95" value={mepRoofPct ? Math.round(mepRoofPct * 100) : ""}
                onChange={(e) => setMepRoofPct((parseFloat(e.target.value) || 0) / 100)}
                style={styles.mepPctInput} data-testid="mep-roof-pct-input" />
              <span style={styles.mepAutoNote}>
                % of roof area {roofArea > 0 ? `(${Math.round(roofArea).toLocaleString()} m²) → ${autoMep.toLocaleString()} m²` : ""}
              </span>
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div style={styles.shadowLabelRow}>
            <label style={{ ...styles.fieldLabel, marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 5 }}>
              Context Shadow
              <InfoTip
                testId="shadow-info"
                title="Context Shadow"
                body="Share of the growable area lost to overshadowing from nearby buildings. With Auto-Calc on, it's estimated from your analysis scope — a denser surrounding context means more shade."
              />
            </label>
            <button type="button" data-testid="shadow-auto-toggle" onClick={() => setShadowAuto(!shadowAuto)}
              style={{ ...styles.autoBtn, ...(shadowAuto ? styles.autoBtnOn : {}) }}
              title="Auto-derive contextual overshadowing from the analysis scope">
              {shadowAuto ? "Auto ✓" : "Auto"}
            </button>
          </div>
          <div style={styles.occupancyRow}>
            <input type="number" min="0" max="95" disabled={shadowAuto}
              value={shadowPct ? Math.round(shadowPct * 100) : ""} placeholder="0"
              onChange={(e) => setShadowPct((parseFloat(e.target.value) || 0) / 100)}
              style={{ ...styles.input, ...(shadowAuto ? styles.inputLocked : {}) }} data-testid="shadow-pct-input" />
            <span style={styles.unit}>%</span>
          </div>
        </div>
      </div>

      {baselines ? (
        <div style={styles.readout} data-testid="occupancy-readout">
          <div style={styles.readoutRow}>
            <span style={styles.rLabel}>Per-capita food coverage</span>
            <span style={{ ...styles.rValue, color: baselines.coveragePct >= 100 ? colors.ok : colors.textPrimary }}>
              {baselines.coveragePct}%
            </span>
          </div>
          <div style={styles.bar}><div style={{ ...styles.barFill, width: `${Math.min(100, baselines.coveragePct)}%` }} /></div>
          <div style={styles.readoutMeta}>
            {Math.round(annualYieldKg)} kg/yr produced vs {baselines.annualFoodNeedKg} kg/yr needed
          </div>
          <div style={styles.baselineGrid}>
            <div style={styles.baseStat}><span style={styles.baseVal}>{baselines.greywaterLpd.toLocaleString()}</span><span style={styles.baseLbl}>L/day greywater</span></div>
            <div style={styles.baseStat}><span style={styles.baseVal}>{baselines.organicWasteKgDay}</span><span style={styles.baseLbl}>kg/day organics</span></div>
          </div>
        </div>
      ) : (
        <div style={styles.hint}>Set the building typology and occupancy to view the per-capita food coverage and waste-to-resource baselines.</div>
      )}


      {opportunity?.viable_area != null && (() => {
        const vb = opportunity.viable_breakdown || {};
        const exposed = opportunity.exposed_area ?? opportunity.gross_total ?? 0;
        const facSel = ["facade_s", "facade_e", "facade_w", "facade_n"].filter((l) => (selectedLocs || []).includes(l));
        const facOrient = facSel.length === 1 ? facSel[0].split("_")[1].toUpperCase() : null;
        const surfaces = [
          { key: "roof", label: "Roof", val: vb.roof, locs: ["rooftop"] },
          { key: "ground", label: "Ground", val: vb.base, locs: ["ground"] },
          { key: "facade", label: facOrient ? `Facade ${facOrient}` : "Facade", val: facOrient ? vb.facade?.[facOrient] : vb.facade?.total, locs: ["facade_s", "facade_e", "facade_w", "facade_n", "balcony"] },
        ];
        if (opportunity.cea) surfaces.push({ key: "interior", label: "Interior · CEA", val: vb.interior, locs: ["indoor"] });
        const isActive = (s) => s.locs.some((l) => (selectedLocs || []).includes(l));
        const active = surfaces.find(isActive);
        return (
          <div style={styles.oppCard} data-testid="opportunity-readout">
            <div style={styles.oppMetricRow}>
              <div style={styles.oppMetric}>
                <span style={styles.oppMetricLbl}>Exposed Envelope Area</span>
                <span style={styles.oppMetricVal} data-testid="exposed-area">{Math.round(exposed).toLocaleString()} m²</span>
                <span style={styles.oppMetricHint}>measured exterior · hidden faces removed</span>
              </div>
              <div style={styles.oppMetricDiv} />
              <div style={styles.oppMetric}>
                <span style={{ ...styles.oppMetricLbl, color: colors.ok }}>Viable Growing Area</span>
                <span style={{ ...styles.oppMetricVal, color: colors.ok }} data-testid="viable-area">{Math.round(opportunity.viable_area).toLocaleString()} m²</span>
                <span style={styles.oppMetricHint}>{Math.round((opportunity.notch_pct || 0) * 100)}% after shadow · climate · reserve</span>
              </div>
            </div>

            {active && active.val != null && (
              <div style={styles.oppContext} data-testid="viable-context">
                Viable {active.label.toLowerCase()} area: <strong>{Math.round(active.val).toLocaleString()} m²</strong>
              </div>
            )}

            <div style={styles.oppChips}>
              {surfaces.map((s) => (
                <div key={s.key}
                  style={{ ...styles.oppChip, ...(isActive(s) ? styles.oppChipOn : {}) }}
                  data-testid={`viable-chip-${s.key}`}>
                  <span style={styles.oppChipLbl}>{s.label}</span>
                  <span style={styles.oppChipVal}>{s.val != null ? `${Math.round(s.val).toLocaleString()} m²` : "—"}</span>
                  {yieldDensity > 0 && s.val != null && (
                    <span style={styles.oppChipYield} data-testid={`chip-yield-${s.key}`}>~{Math.round(s.val * yieldDensity).toLocaleString()} kg/yr</span>
                  )}
                </div>
              ))}
            </div>

            {opportunity.cea && (
              <div style={styles.oppCea} data-testid="cea-note">
                Controlled Environment Agriculture on — interior surfaces counted (+{Math.round(vb.interior || 0).toLocaleString()} m²).
              </div>
            )}

            {opportunity.breakdown?.reserved_roof != null && (
              <div style={styles.oppMep} data-testid="opportunity-mep">
                −{opportunity.breakdown.reserved_roof.toLocaleString()} m² reserved (Roof Reserve {opportunity.breakdown.mep_reserve || 0} m² + {Math.max(0, Math.round((opportunity.breakdown.reserved_roof - (opportunity.breakdown.mep_reserve || 0)))).toLocaleString()} m² typology roof reserve)
                {opportunity.breakdown.shadow_pct > 0 && ` · −${Math.round(opportunity.breakdown.shadow_pct * 100)}% shadow`}
                {opportunity.breakdown.climate_factor != null && ` · climate ×${opportunity.breakdown.climate_factor}`}
              </div>
            )}
          </div>
        );
      })()}
    </section>
  );
}

const styles = {
  card: { background: colors.surface, borderRadius: 16, border: `1px solid ${colors.border}`, boxShadow: shadows.soft, padding: 16, marginBottom: 18 },
  head: { marginBottom: 12 },
  title: { fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.4, color: colors.textSecondary, fontWeight: 700, display: "block" },
  sub: { fontFamily: fonts.sans, fontSize: 11, color: colors.textTertiary, marginTop: 2, display: "block" },
  fieldLabel: { fontFamily: fonts.sans, fontSize: 11, fontWeight: 600, color: colors.textTertiary, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 5, marginTop: 10 },
  select: { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.sans, fontSize: 13, color: colors.textPrimary, cursor: "pointer", appearance: "auto" },
  occupancyRow: { display: "flex", alignItems: "center", gap: 8 },
  occHeadRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 },
  occModeToggle: { display: "inline-flex", background: "rgba(0,0,0,0.05)", borderRadius: 999, padding: 2 },
  occModeBtn: { padding: "4px 10px", borderRadius: 999, border: "none", background: "transparent", fontFamily: fonts.sans, fontSize: 10.5, fontWeight: 700, color: colors.textTertiary, cursor: "pointer" },
  occModeBtnOn: { background: "#fff", color: colors.textPrimary, boxShadow: shadows.sm || "0 1px 3px rgba(0,0,0,0.12)" },
  densityWrap: { display: "flex", flexDirection: "column", gap: 8 },
  densityRow: { display: "flex", gap: 10 },
  densityCol: { flex: 1, display: "flex", flexDirection: "column", gap: 3 },
  densityMini: { fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 0.3, textTransform: "uppercase", color: colors.textTertiary },
  densityResult: { fontFamily: fonts.sans, fontSize: 12, color: colors.textPrimary, padding: "7px 10px", borderRadius: 8, background: "rgba(10,132,255,0.06)", border: "1px solid rgba(10,132,255,0.18)" },
  densitySrc: { fontFamily: fonts.mono, fontSize: 8.5, lineHeight: 1.4, color: colors.textTertiary },
  mepRow: { display: "flex", gap: 12, marginTop: 4 },
  input: { flex: 1, minWidth: 0, padding: "10px 12px", borderRadius: 10, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.sans, fontSize: 13, color: colors.textPrimary },
  inputLocked: { background: "rgba(0,0,0,0.04)", color: colors.textTertiary, cursor: "not-allowed" },
  shadowLabelRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 },
  autoBtn: { padding: "3px 8px", borderRadius: 999, borderWidth: 1, borderStyle: "solid", borderColor: colors.border, background: "#fff", fontFamily: fonts.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: colors.textSecondary, cursor: "pointer" },
  autoBtnOn: { background: colors.pillar.location, borderColor: colors.pillar.location, color: "#fff" },
  autoBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  mepAutoRow: { display: "flex", alignItems: "center", gap: 7, marginTop: 6 },
  mepPctInput: { width: 52, padding: "5px 7px", borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, color: colors.textPrimary, textAlign: "right" },
  mepAutoNote: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.2, color: colors.textTertiary, lineHeight: 1.3 },
  unit: { fontFamily: fonts.sans, fontSize: 12, color: colors.textTertiary },
  readout: { marginTop: 14, padding: 12, borderRadius: 12, background: "rgba(52,199,89,0.06)", border: "1px solid rgba(52,199,89,0.20)" },
  readoutRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  rLabel: { fontFamily: fonts.sans, fontSize: 12, color: colors.textSecondary, fontWeight: 500 },
  rValue: { fontFamily: fonts.sans, fontSize: 20, fontWeight: 700, letterSpacing: -0.5 },
  bar: { height: 6, borderRadius: 999, background: "rgba(0,0,0,0.07)", marginTop: 8, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 999, background: colors.ok, transition: "width 0.4s ease" },
  readoutMeta: { fontFamily: fonts.mono, fontSize: 10, color: colors.textTertiary, marginTop: 6 },
  baselineGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 },
  baseStat: { background: "#fff", borderRadius: 10, padding: "8px 10px", border: `1px solid ${colors.border}` },
  baseVal: { fontFamily: fonts.sans, fontSize: 15, fontWeight: 700, color: colors.textPrimary, display: "block" },
  baseLbl: { fontFamily: fonts.mono, fontSize: 9, letterSpacing: 0.3, color: colors.textTertiary, textTransform: "uppercase" },
  hint: { marginTop: 12, padding: "10px 12px", borderRadius: 10, border: `1px dashed ${colors.borderStrong}`, fontFamily: fonts.sans, fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 },
  oppCard: { marginTop: 12, padding: 14, borderRadius: 14, background: "rgba(52,199,89,0.05)", border: "1px solid rgba(52,199,89,0.22)" },
  oppMetricRow: { display: "flex", alignItems: "stretch", gap: 12 },
  oppMetric: { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  oppMetricDiv: { width: 1, background: "rgba(0,0,0,0.08)", alignSelf: "stretch" },
  oppMetricLbl: { fontFamily: fonts.mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color: colors.textTertiary, fontWeight: 700 },
  oppMetricVal: { fontFamily: fonts.sans, fontSize: 21, fontWeight: 800, letterSpacing: -0.6, color: colors.textPrimary, lineHeight: 1.1 },
  oppMetricHint: { fontFamily: fonts.sans, fontSize: 10, color: colors.textTertiary, lineHeight: 1.3 },
  oppContext: { marginTop: 11, padding: "8px 11px", borderRadius: 9, background: colors.pillar.location, color: "#fff", fontFamily: fonts.sans, fontSize: 12, fontWeight: 500 },
  oppChips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 11 },
  oppChip: { flex: "1 1 30%", minWidth: 78, padding: "7px 9px", borderRadius: 9, background: "#fff", border: `1px solid ${colors.border}`, display: "flex", flexDirection: "column", gap: 2, transition: "border-color 0.2s ease, box-shadow 0.2s ease" },
  oppChipOn: { borderColor: colors.pillar.location, boxShadow: `0 0 0 2px ${colors.pillar.location}22` },
  oppChipLbl: { fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 0.3, textTransform: "uppercase", color: colors.textTertiary, fontWeight: 700 },
  oppChipVal: { fontFamily: fonts.sans, fontSize: 13.5, fontWeight: 700, color: colors.textPrimary },
  oppChipYield: { fontFamily: fonts.mono, fontSize: 9, fontWeight: 700, color: colors.ok, marginTop: 1, whiteSpace: "nowrap" },
  oppCea: { marginTop: 10, fontFamily: fonts.sans, fontSize: 11, color: "#8a5a00", background: "rgba(245,180,20,0.12)", border: "1px solid rgba(245,180,20,0.4)", borderRadius: 8, padding: "7px 10px", lineHeight: 1.35 },
  oppMep: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 0.2, color: colors.textTertiary, marginTop: 9, lineHeight: 1.4 },
};
